/**
 * storyVisitTracker.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Tracks how many times each story/question page is visited within one
 * assessment session. Used to generate versioned gaze-recording IDs so
 * that data from each revisit is stored separately instead of overwriting
 * the previous visit.
 *
 * Storage: sessionStorage — resets on full page reload (new assessment).
 * ─────────────────────────────────────────────────────────────────────────────
 */

const STORAGE_KEY = 'dystest_story_visits';

function loadCounts(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function saveCounts(data: Record<string, number>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* silent */ }
}

/**
 * Increment the visit counter for a page and return the NEW count.
 * Call once when the page component mounts (inside a useRef so StrictMode
 * double-mount does not double-count).
 */
export function incrementVisit(pageId: string): number {
  const counts = loadCounts();
  const next = (counts[pageId] ?? 0) + 1;
  counts[pageId] = next;
  saveCounts(counts);
  return next;
}

/**
 * Start a visit of a story page and return its number, based on the visits that were actually saved:
 * - a counted visit that never saved any data (a mount that was thrown away and redone, 02-10-2026:
 *   story 1 of assess_274b3dc141d7 started at visit 2 with no visit 1 anywhere) is reused, not skipped;
 * - after a resumed session (counter empty, visits restored) the next number follows the highest saved
 *   visit instead of restarting at 1 and overwriting it.
 */
export function startVisit(pageId: string, savedVisits: Iterable<number>): number {
  const counts = loadCounts();
  const saved = Math.max(0, ...savedVisits);
  const current = Math.max(counts[pageId] ?? 0, saved);
  const next = current > saved ? current : current + 1;
  counts[pageId] = next;
  saveCounts(counts);
  return next;
}

/** Visit numbers saved for a story page, from the saved page ids ('story1' counts as visit 1, 'story1_v3' as 3). */
export function savedVisitNumbers(pageIds: Iterable<string>, baseId: string): number[] {
  const out: number[] = [];
  for (const id of pageIds) {
    if (id === baseId) out.push(1);
    else if (id.startsWith(`${baseId}_v`)) {
      const n = Number(id.slice(baseId.length + 2));
      if (Number.isInteger(n) && n > 0) out.push(n);
    }
  }
  return out;
}

/**
 * Return the current visit count for a page without incrementing.
 * Returns 0 if the page has never been visited this session.
 */
export function getVisitCount(pageId: string): number {
  return loadCounts()[pageId] ?? 0;
}

/** Reset all counters (call on new assessment start). */
export function clearVisits(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/** Build the versioned storage key: e.g. 'story1_v2' */
export function versionedId(baseId: string, visitNum: number): string {
  return `${baseId}_v${visitNum}`;
}
